import { produce } from 'immer'
import { MetricsSeries } from 'src/models/spendMetrics.models'
import { UserAssociation, UserListResponse } from 'src/models/user.models'
import { budgetOverviewRouteProps } from 'src/navigation/routeProps'
import {
  DELETE_BUDGET_USERS_FAILURE,
  DELETE_BUDGET_USERS_INIT,
  DELETE_BUDGET_USERS_SUCCESS,
  DeleteBudgetUsersActions,
  GET_CREDIT_CARDS_FAILURE,
  GET_CREDIT_CARDS_INIT,
  GET_CREDIT_CARDS_SUCCESS,
  GetCreditCardsActions,
  PatchCreditCardStatusActions,
  POST_CREDIT_CARD_FAILURE,
  POST_CREDIT_CARD_START_VERIFICATION_FAILURE,
  POST_CREDIT_CARD_START_VERIFICATION_INIT,
  POST_CREDIT_CARD_START_VERIFICATION_SUCCESS,
  POST_CREDIT_CARD_SUCCESS,
  POST_CREDIT_CARD_VERIFY_FAILURE,
  POST_CREDIT_CARD_VERIFY_INIT,
  POST_CREDIT_CARD_VERIFY_SUCCESS,
  PostCreditCardActions,
  PostCreditCardStartVerificationFailureAction,
  PostCreditCardStartVerificationInitAction,
  PostCreditCardStartVerificationSuccessAction,
  PostCreditCardVerifyFailureAction,
  PostCreditCardVerifyInitAction,
  PostCreditCardVerifySuccessAction,
} from 'src/redux/actions/data/creditCards.actions'
import {
  POST_SPEND_METRICS_FAILURE,
  POST_SPEND_METRICS_INIT,
  POST_SPEND_METRICS_SUCCESS,
  PostSpendMetricsActions,
} from 'src/redux/actions/data/transactions.actions'
import {
  GET_RECENT_USERS_SUCCESS,
  GetRecentUsersActions,
} from 'src/redux/actions/data/users.actions'
import {
  GET_VIRTUAL_CARDS_FAILURE,
  GET_VIRTUAL_CARDS_INIT,
  GET_VIRTUAL_CARDS_SUCCESS,
  GetVirtualCardsActions,
} from 'src/redux/actions/data/virtualCards.actions'
import CreditCardsSnapshotPaginatedDataReducer, {
  Actions as CreditCardsSnapshotPaginatedDataActions,
  CreditCardsSnapshotPaginatedDataState,
  initialState as initialCreditCardsSnapshotPaginatedDataState,
} from 'src/redux/creditCardsSnapshotPaginatedData.reducer'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import { RecentUsersFullPaginatedDataState } from 'src/redux/recentUsersFullPaginatedData.reducer'
import UsersFullPaginatedDataReducer, {
  Actions as UsersFullPaginatedDataActions,
  initialState as initialUsersFullPaginatedDataState,
  UsersFullPaginatedDataState,
} from 'src/redux/usersFullPaginatedData.reducer'
import VirtualCardsSnapshotPaginatedDataReducer, {
  Actions as VirtualCardsSnapshotPaginatedDataActions,
  initialState as initialVirtualCardsSnapshotPaginatedDataState,
  VirtualCardsSnapshotPaginatedDataState,
} from 'src/redux/virtualCardsSnapshotPaginatedData.reducer'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface BudgetOverviewBaseState {
  postingBudgetVerification?: boolean
  budgetVerificationError?: string
  postingResendBudgetVerification?: boolean
  budgetResendVerificationError?: string
  spendMetricsLoading: boolean
  dataSets?: number[]
  series?: MetricsSeries[]
  fetchingVirtualCards: boolean
  fetchingCreditCards: boolean
  recentUsers: UserListResponse[]
  recentUsersFullPaginatedData: RecentUsersFullPaginatedDataState
  updateBudgetError?: string
  removingBudgetUser: boolean
  removingBudgetUserTopLevelError?: string
}

export const initialBaseState: BudgetOverviewBaseState = {
  spendMetricsLoading: false,
  fetchingVirtualCards: false,
  fetchingCreditCards: false,
  recentUsers: [],
  recentUsersFullPaginatedData: {
    ids: [],
    loading: false,
    complete: false,
  },
  removingBudgetUser: false,
}

export type BaseActions =
  | PostSpendMetricsActions
  | PatchCreditCardStatusActions
  | PostCreditCardVerifyFailureAction
  | PostCreditCardVerifySuccessAction
  | PostCreditCardVerifyInitAction
  | PostCreditCardStartVerificationInitAction
  | PostCreditCardStartVerificationFailureAction
  | PostCreditCardStartVerificationSuccessAction
  | GetCreditCardsActions
  | GetVirtualCardsActions
  | GetRecentUsersActions
  | PostCreditCardActions
  | DeleteBudgetUsersActions

const BaseBudgetOverviewReducer = (
  state = initialBaseState,
  action: BaseActions,
): BudgetOverviewBaseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case POST_SPEND_METRICS_INIT: {
        draft.spendMetricsLoading = true
        return draft
      }

      case POST_SPEND_METRICS_SUCCESS: {
        draft.spendMetricsLoading = false
        draft.dataSets = action.payload.dataSets
        draft.series = action.payload.series
        return draft
      }

      case POST_SPEND_METRICS_FAILURE: {
        draft.spendMetricsLoading = false
        return draft
      }

      case POST_CREDIT_CARD_VERIFY_INIT: {
        draft.postingBudgetVerification = true
        return draft
      }

      case POST_CREDIT_CARD_VERIFY_FAILURE: {
        draft.budgetVerificationError = action.payload.error
        draft.postingBudgetVerification = false

        return draft
      }

      case POST_CREDIT_CARD_VERIFY_SUCCESS: {
        draft.postingBudgetVerification = false
        return draft
      }

      case POST_CREDIT_CARD_START_VERIFICATION_INIT: {
        draft.postingResendBudgetVerification = true
        return draft
      }

      case POST_CREDIT_CARD_START_VERIFICATION_FAILURE: {
        draft.budgetResendVerificationError = action.payload.error
        draft.postingResendBudgetVerification = false
        return draft
      }

      case POST_CREDIT_CARD_START_VERIFICATION_SUCCESS: {
        draft.postingResendBudgetVerification = false
        return draft
      }

      case GET_VIRTUAL_CARDS_INIT: {
        draft.fetchingVirtualCards = true
        return draft
      }
      case GET_VIRTUAL_CARDS_SUCCESS: {
        draft.fetchingVirtualCards = false
        return draft
      }

      case GET_VIRTUAL_CARDS_FAILURE: {
        draft.fetchingVirtualCards = false
        return draft
      }

      case GET_CREDIT_CARDS_INIT: {
        draft.fetchingCreditCards = true
        return draft
      }
      case GET_CREDIT_CARDS_SUCCESS: {
        draft.fetchingCreditCards = false
        return draft
      }

      case GET_CREDIT_CARDS_FAILURE: {
        draft.fetchingCreditCards = false
        return draft
      }

      case POST_CREDIT_CARD_SUCCESS: {
        draft.updateBudgetError = undefined
        return draft
      }

      case POST_CREDIT_CARD_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.updateBudgetError = errors.topLevelError
        return draft
      }

      case GET_RECENT_USERS_SUCCESS: {
        draft.recentUsers = action.payload.users
        draft.recentUsersFullPaginatedData.ids =
          action.userAssociation === UserAssociation.invitationTo
            ? [
                // always puts invitationTo association below everything else
                ...draft.recentUsersFullPaginatedData.ids,
                ...action.payload.users.map((user) => user.id),
              ]
            : [
                ...action.payload.users.map((user) => user.id),
                ...draft.recentUsersFullPaginatedData.ids,
              ]
        draft.recentUsersFullPaginatedData.complete =
          action.payload.pagination.page >=
          action.payload.pagination.numberOfPages - 1
        draft.recentUsersFullPaginatedData.page = action.payload.pagination.page
        draft.recentUsersFullPaginatedData.numPages =
          action.payload.pagination.numberOfPages
        draft.recentUsersFullPaginatedData.totalItems =
          action.payload.pagination.totalItems
        return draft
      }

      case DELETE_BUDGET_USERS_INIT: {
        draft.removingBudgetUser = true
        draft.removingBudgetUserTopLevelError = undefined
        return draft
      }

      case DELETE_BUDGET_USERS_SUCCESS: {
        draft.removingBudgetUser = false
        return draft
      }

      case DELETE_BUDGET_USERS_FAILURE: {
        draft.removingBudgetUser = false
        draft.removingBudgetUserTopLevelError = action.payload.error
        return draft
      }
    }
  })
}

export type BudgetOverviewState = BudgetOverviewBaseState & {
  creditCardsSnapshotPaginatedData: CreditCardsSnapshotPaginatedDataState
  virtualCardsSnapshotPaginatedData: VirtualCardsSnapshotPaginatedDataState
  usersFullPaginatedData: UsersFullPaginatedDataState
}

export const initialState: BudgetOverviewState = {
  ...initialBaseState,
  creditCardsSnapshotPaginatedData:
    initialCreditCardsSnapshotPaginatedDataState,
  virtualCardsSnapshotPaginatedData:
    initialVirtualCardsSnapshotPaginatedDataState,
  usersFullPaginatedData: initialUsersFullPaginatedDataState,
}

export type Actions =
  | BaseActions
  | CreditCardsSnapshotPaginatedDataActions
  | VirtualCardsSnapshotPaginatedDataActions
  | UsersFullPaginatedDataActions

const CoreBudgetOverviewReducer = (
  state = initialState,
  action: Actions,
): BudgetOverviewState => ({
  ...BaseBudgetOverviewReducer(state, action as BaseActions),
  creditCardsSnapshotPaginatedData: CreditCardsSnapshotPaginatedDataReducer(
    state.creditCardsSnapshotPaginatedData,
    action as CreditCardsSnapshotPaginatedDataActions,
  ),
  virtualCardsSnapshotPaginatedData: VirtualCardsSnapshotPaginatedDataReducer(
    state.virtualCardsSnapshotPaginatedData,
    action as VirtualCardsSnapshotPaginatedDataActions,
  ),
  usersFullPaginatedData: UsersFullPaginatedDataReducer(
    state.usersFullPaginatedData,
    action as UsersFullPaginatedDataActions,
  ),
})

const BudgetOverviewReducer = createBackgroundReducer(
  budgetOverviewRouteProps,
  initialState,
  CoreBudgetOverviewReducer,
)

export default BudgetOverviewReducer
